// ============================================================
// PICKUP ANALYTICS — Customer pickup rates, delays & trends
// ============================================================

import React, { useState, useEffect } from 'react';
import { PackageCheck, RefreshCw, TrendingUp, Clock } from 'lucide-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, LineChart, Line, Legend,
} from 'recharts';
import { Button, StatCard, Table } from '../components/ui';
import { getPickupAnalytics, getPeakHoursData, getPickupStatusBreakdown } from '../services/mockData';
import { adminApi } from '../services/apiService';
import { formatNumber } from '../utils/helpers';

const PIE_COLORS = ['#10B981', '#EF4444', '#F59E0B', '#8B5CF6', '#3B82F6'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--admin-card)', border: '1px solid var(--admin-border)',
      borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color }}>
          {p.name}: {p.value}
        </div>
      ))}
    </div>
  );
};

const PickupAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [peakData, setPeakData]   = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState('overview');

  const load = async () => {
    setLoading(true);
    const [data, peak, bd] = await Promise.all([
      adminApi.getPickupAnalytics(getPickupAnalytics),
      adminApi.getPeakHours(getPeakHoursData),
      Promise.resolve(getPickupStatusBreakdown()),
    ]);
    setAnalytics(data || getPickupAnalytics());
    setPeakData(Array.isArray(peak) ? peak : getPeakHoursData());
    setBreakdown(Array.isArray(bd) ? bd : []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (loading || !analytics) {
    return <div style={{ padding: 48, textAlign: 'center', color: '#64748B' }}>Loading analytics…</div>;
  }

  const {
    overallPickupRate, noShowRate, avgPickupDelay, repeatPickupRate,
    delayBuckets = [], monthlyTrend = [], cityBreakdown = [],
  } = analytics;

  const cityColumns = [
    { key: 'city',        label: 'City',       render: v => <b>{v}</b> },
    { key: 'totalOrders', label: 'Orders',     render: v => formatNumber(v) },
    { key: 'pickupRate',  label: 'Pickup Rate', render: v => (
      <span style={{ fontWeight: 700, color: v >= 90 ? '#10B981' : v >= 75 ? '#F59E0B' : '#EF4444' }}>
        {v.toFixed(1)}%
      </span>
    )},
    { key: 'noShowRate',  label: 'No-Show',    render: v => (
      <span style={{ color: v > 10 ? '#EF4444' : '#94A3B8' }}>{v.toFixed(1)}%</span>
    )},
    { key: 'avgDelay',    label: 'Avg Delay',  render: v => `${v} min` },
  ];

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Pickup Analytics</h1>
          <p className="admin-page-subtitle">Customer pickup behaviour, completion rates, and delay analysis</p>
        </div>
        <Button variant="secondary" icon={<RefreshCw size={14} />} onClick={load}>Refresh</Button>
      </div>

      {/* Summary cards */}
      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
        <StatCard label="Pickup Completion" value={`${overallPickupRate?.toFixed(1) || '—'}%`}
          icon={<PackageCheck size={18} />} color="#10B981" />
        <StatCard label="No-Show Rate"      value={`${noShowRate?.toFixed(1) || '—'}%`}
          icon={<TrendingUp size={18} />} color="#EF4444" />
        <StatCard label="Avg Pickup Delay"  value={`${avgPickupDelay?.toFixed(0) || '—'} min`}
          icon={<Clock size={18} />} color="#F59E0B" />
        <StatCard label="Repeat Pickup Rate" value={`${repeatPickupRate?.toFixed(0) || '—'}%`}
          icon={<PackageCheck size={18} />} color="#8B5CF6" />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid var(--admin-border)' }}>
        {[['overview','Overview'], ['delay','Delay Analysis'], ['peak','Peak Hours'], ['city','By City']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '8px 20px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: tab === key ? 700 : 400,
            color: tab === key ? '#E85D04' : '#64748B',
            borderBottom: tab === key ? '2px solid #E85D04' : '2px solid transparent',
            fontSize: 14,
          }}>{label}</button>
        ))}
      </div>

      {tab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {/* Pickup status pie */}
          <div className="admin-card">
            <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 15 }}>Pickup Status Breakdown</h3>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={breakdown} dataKey="value" nameKey="name" cx="50%" cy="50%"
                  innerRadius={55} outerRadius={90} paddingAngle={3} label={({ name, percent }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`}>
                  {breakdown.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: 'var(--admin-card)', border: '1px solid var(--admin-border)' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Monthly trend */}
          <div className="admin-card">
            <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 15 }}>Monthly Pickup Rate Trend</h3>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={monthlyTrend} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis unit="%" domain={[50, 100]} tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="pickupRate" name="Pickup Rate" stroke="#10B981" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="noShowRate" name="No-Show Rate" stroke="#EF4444" strokeWidth={1.5} dot={false} strokeDasharray="4 4" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {tab === 'delay' && (
        <div className="admin-card">
          <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 15 }}>Pickup Delay Distribution</h3>
          <p style={{ marginTop: 0, marginBottom: 20, fontSize: 13, color: '#94A3B8' }}>
            Time between order being 'Ready for Pickup' and customer actually picking up
          </p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={delayBuckets} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
              <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} label={{ value: 'Orders', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="orders" name="Orders" fill="#8B5CF6" maxBarSize={50} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {tab === 'peak' && (
        <div className="admin-card">
          <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 15 }}>Peak Hours — Pickup Volume</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={peakData} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
              <XAxis dataKey="hour" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="orders" name="Orders" fill="#E85D04" maxBarSize={30} radius={[3,3,0,0]} />
              <Bar dataKey="avgWaitMins" name="Avg Wait (min)" fill="#8B5CF6" maxBarSize={30} radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {tab === 'city' && (
        <div className="admin-table-wrapper">
          <div className="admin-table-toolbar">
            <span style={{ fontWeight: 600, fontSize: 14 }}>Pickup Performance by City</span>
          </div>
          <Table columns={cityColumns} data={cityBreakdown} />
        </div>
      )}
    </div>
  );
};

export default PickupAnalytics;
